/**
 * OrderInvestigationTimeline — main container component.
 *
 * Fetches the investigation case for an order and renders:
 *   1. CaseSummaryHeader (badge counts + message)
 *   2. List of 8 MilestoneRow components (always shown)
 *
 * Integration: drop into the Order Detail modal with:
 *   <OrderInvestigationTimeline orderId="entity-ulid" displayName="QARGO-12345" />
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import type { OrderInvestigationCase } from '../../types/orderInvestigation';
import { buildInvestigationCase } from '../../services/orderInvestigationAdapter';
import CaseSummaryHeader from './CaseSummaryHeader';
import MilestoneRow from './MilestoneRow';
import { Button } from '@/components/ui/button';

interface OrderInvestigationTimelineProps {
  /** Truth-layer entity ID (ULID) for the order */
  orderId: string;
  /** Human-readable display name (e.g. Qargo order_id) */
  displayName?: string;
}

export default function OrderInvestigationTimeline({
  orderId,
  displayName,
}: OrderInvestigationTimelineProps) {
  const [data, setData] = useState<OrderInvestigationCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await buildInvestigationCase(orderId, displayName);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load investigation data');
    } finally {
      setLoading(false);
    }
  }, [orderId, displayName]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-secondary/50 rounded-lg p-4 border border-border">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-brand" />
          <h4 className="text-lg font-semibold text-white">Order Investigation Timeline</h4>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={load}
          disabled={loading}
          className="rounded-lg hover:bg-accent/50"
          title="Refresh investigation"
        >
          <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Loading state */}
      {loading && !data && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-6 h-6 rounded-full bg-accent/50 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-accent/50 rounded w-2/3" />
                <div className="h-3 bg-accent/30 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && !data && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
          <p className="text-destructive text-sm mb-2">{error}</p>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={load}
            className="h-auto p-0 text-destructive hover:text-destructive"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Investigation case */}
      {data && (
        <div className="space-y-4">
          {/* Summary header */}
          <CaseSummaryHeader summary={data.summary} />

          {/* Milestones */}
          <div className="space-y-0">
            {data.milestones.map((milestone, i) => (
              <MilestoneRow
                key={milestone.code}
                milestone={milestone}
                isLast={i === data.milestones.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
